/**
 * @author Fernando Ruano
 * @file saga.js
 * @description Profile Saga
 */

// Dependencies
import { put, call, takeLatest } from 'redux-saga/effects';

// Global actions
import { setResponse, setLoading } from '@nephro/redux/stores/app/actions';

// Services
import profileService from '@nephro/services/profile-service';

// Constants
import {
  CREATE_PROFILE,
  DELETE_PROFILE_BY_ID,
  GET_ALL_PROFILES,
  GET_PROFILE_BY_ID,
  UPDATE_PROFILE
} from './constants';

const customError = {
  type: 'Error',
  message: 'commons.problem'
};

/**
 * Call service to get profiles from backend
 * @param { object } action - Action with payload data
 */
export function* getProfilesArray(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { paged, page, limit } = data;

  try {
    const response = yield call(profileService.getProfiles, paged, limit, page);
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Call service to get profile info by id
 * @param { object } action - Action with payload data
 */
export function* getProfileById(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { id } = data;

  try {
    const response = yield call(profileService.getProfileById, id);
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        'Error',
        'errors.profileNotFound' || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
}

/**
 * Call service to create profile
 * @param { object } action - Action with payload data
 */
export function* createProfile(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { name, description } = data;

  try {
    const response = yield call(
      profileService.createProfile,
      name,
      description
    );
    yield put(
      setResponse(
        'success',
        'new profile',
        'profileMg.profileCreated',
        response,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Call service to update profile
 * @param { object } action - Action with payload data
 */
export function* updateProfile(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { name, description, id } = data;

  try {
    const response = yield call(
      profileService.updateProfile,
      name,
      description,
      id
    );
    yield put(
      setResponse(
        'success',
        'updated profile',
        'profileMg.profileUpdated',
        response,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Call service to delete profile by id
 * @param { object } action - Action with payload data
 */
export function* deleteProfileById(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { id } = data;

  try {
    const response = yield call(profileService.deleteProfileById, id);
    yield put(
      setResponse(
        'success',
        'deleted profile',
        'profileMg.profileDeleted',
        { ...response },
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        'Error',
        `errors.${err.code}` || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Declare all component sagas
 */
export function* watchProfileSaga() {
  yield takeLatest(GET_ALL_PROFILES, getProfilesArray);
  yield takeLatest(GET_PROFILE_BY_ID, getProfileById);
  yield takeLatest(CREATE_PROFILE, createProfile);
  yield takeLatest(UPDATE_PROFILE, updateProfile);
  yield takeLatest(DELETE_PROFILE_BY_ID, deleteProfileById);
}
